/**
 * Central registry of all backend API endpoint paths.
 * Import from here instead of hard-coding strings in api/*.ts files.
 *
 * The /api/v1 prefix is appended by the API client from EXPO_PUBLIC_API_BASE_URL,
 * so do NOT repeat it here.
 *
 * Parameterised segments use a factory pattern:
 *   endpoint.review.application(id)  → "/admin/applications/abc123"
 */

const endpoint = Object.freeze({
  // ─── Auth ─────────────────────────────────────────────────────────────────
  auth: Object.freeze({
    login: "/auth/login",
    otpRequest: "/auth/otp/request",
    otpVerify: "/auth/otp/verify",
    me: "/auth/me",
    verifyInviteToken: "/auth/verify-invite-token",
    invite: "/auth/invite",
    logout: "/auth/logout",
    changePassword: "/auth/change-password",
    forgotPassword: "/auth/forgot-password",
    resetPassword: "/auth/reset-password",
  }),

  // ─── Users ────────────────────────────────────────────────────────────────
  // NOTE: POST /users is the vendor user registration endpoint (used by auth flow).
  // Admin/staff user management lives under adminUsers.
  users: Object.freeze({
    create: "/users",
  }),

  // ─── Admin Users ──────────────────────────────────────────────────────────
  adminUsers: Object.freeze({
    list: "/admin/users",
    create: "/admin/users",
    detail: (id: string) => `/admin/users/${id}`,
    update: (id: string) => `/admin/users/${id}`,
    activityLog: (id: string) => `/admin/users/${id}/activity`,
    deactivate: (id: string) => `/admin/users/${id}/deactivate`,
    reactivate: (id: string) => `/admin/users/${id}/reactivate`,
  }),

  // ─── Admin Stats ──────────────────────────────────────────────────────────
  adminStats: "/admin/stats",

  // ─── Vendors ──────────────────────────────────────────────────────────────
  vendors: Object.freeze({
    register: "/vendors/register",
    invite: "/vendors/invite",
    me: "/vendors/me",
    setLocale: "/vendors/me/locale",
    // Vendor self-service — require {applicationId} from /auth/me
    application: (id: string) => `/vendors/applications/${id}`,
    applicationActivity: (id: string) => `/vendors/applications/${id}/activity`,
    applicationFieldReviews: (id: string) => `/vendors/applications/${id}/field-reviews`,
    applicationFieldReview: (id: string) => `/vendors/applications/${id}/field-review`,
    applicationFieldEdit: (id: string) => `/vendors/applications/${id}/field-edit`,
  }),

  // ─── Pre-vetting ──────────────────────────────────────────────────────────
  preVetting: Object.freeze({
    draft: "/vendors/me/pre-vetting/draft",
    submit: "/vendors/me/pre-vetting/submit",
    upload: "/vendors/me/documents/upload",
    documentAccess: "/vendors/me/documents/access",
  }),

  // ─── Review / Admin ───────────────────────────────────────────────────────
  review: Object.freeze({
    stats: "/admin/applications/stats",
    applications: "/admin/applications",
    application: (id: string) => `/admin/applications/${id}`,
    applicationDecision: (id: string) => `/admin/applications/${id}/decision`,
    applicationFieldReview: (id: string) => `/admin/applications/${id}/field-review`,
    applicationFieldEdit: (id: string) => `/admin/applications/${id}/field-edit`,
    applicationFieldReviews: (id: string) => `/admin/applications/${id}/field-reviews`,
    applicationActivity: (id: string) => `/admin/applications/${id}/activity`,
    applicationUndoDecision: (id: string) => `/admin/applications/${id}/undo-decision`,
    applicationFieldConfig: "/admin/applications/field-config",
    documentAccess: (id: string) => `/admin/applications/${id}/documents/access`,
  }),

  // ─── Invitations ──────────────────────────────────────────────────────────
  invitations: Object.freeze({
    list: "/invitations",
    detail: (id: string) => `/invitations/${id}`,
    send: "/invitations",
    resend: (id: string) => `/invitations/${id}/resend`,
  }),

  // ─── Localization ─────────────────────────────────────────────────────────
  localization: Object.freeze({
    bundle: "/localization/bundle",
    entries: "/localization/entries",
    entry: (key: string) => `/localization/entries/${encodeURIComponent(key)}`,
    import: "/localization/import",
    statistics: "/localization/statistics",
    industries: "/localization/industries",
    industryEntry: (id: string) => `/localization/industries/${id}`,
    productCategories: "/localization/product-categories",
    productCategoryEntry: (id: string) => `/localization/product-categories/${id}`,
    entryActivity: (key: string) => `/localization/entries/${encodeURIComponent(key)}/activity`,
  }),
});

export default endpoint;
